import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  IconButton,
  Avatar,
  Button,
  CircularProgress,
} from "@mui/material";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, FormikProvider, useFormik, FieldArray } from "formik";
import { IMAGE_BASE } from "../../utils/constants";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { toast } from "react-toastify";
import { AddCircle, RemoveCircle } from "@mui/icons-material";
import errorHandler from "../../utils/errorHandler";
import { BidPackageSchema } from "../../utils/Schema";
import { bitPackageServices } from "../../services/AllServices";

function AddEditBidPackageManagement() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [data, setData] = useState();

  const fileInputRef = useRef(null);
  const ALLOWED_CHARACTERS_REGEX = /^[A-Za-z0-9\s]*$/;

  const handleKeyPress = (e) => {
    if (!ALLOWED_CHARACTERS_REGEX.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageLoading(true);
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setImageLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await bitPackageServices.getById({ id });
        const resData = response?.data?.data;
        setImagePreview(IMAGE_BASE + resData?.image);
        setData(resData);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: BidPackageSchema,
    initialValues: {
      name: data?.name || "",
      price: data?.price || "",
      earnBids: data?.earnBids || "",
      description: data?.description || [""],
      image: data?.image || null,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const formData = new FormData();
        if (id) formData.append("id", id);
        formData.append("name", values.name.trim());
        formData.append("price", values.price);
        formData.append("earnBids", values.earnBids);
    
        // Append description as JSON string or separate keys
        values.description.forEach((desc, index) => {
          formData.append(`description[${index}]`, desc);
        });
    
        // Append image if new file selected
        if (selectedImage) {
          formData.append("image", selectedImage);
        }
    
        const response = id
          ? await bitPackageServices.edit(formData)
          : await bitPackageServices.add(formData);
    
        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
    
    // onSubmit: async (values) => {
    //   setLoading(true);
    //   try {
    //     const payload = {
    //       id: id || undefined,
    //       name: values.name.trim(),
    //       price: values.price,
    //       earnBids: values.earnBids,
    //       description: values.description,
    //       image: selectedImage || values.image,
    //     };

    //     const response = id
    //       ? await bitPackageServices.edit(payload)
    //       : await bitPackageServices.add(payload);

    //     toast.success(response?.data?.message, { toastId: "success" });
    //     navigate(-1);
    //   } catch (error) {
    //     errorHandler(error);
    //   } finally {
    //     setLoading(false);
    //   }
    // },
  });

  return (
    <Box>
      {/* <Helmet> */}
      <title>{id ? "Edit" : "Add"} bid-package-management</title>
      {/* </Helmet> */}
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Bid`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>

              <Grid item xs={12} md={12}>
                  <input
                    accept="image/*"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                  <Box
                    mt={2}
                    onClick={() => fileInputRef.current.click()}
                    sx={{
                      width: 120,
                      height: 120,
                      mx: "auto",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #ccc",
                      borderRadius: "50%",
                    }}
                  >
                    {imageLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Avatar
                        src={imagePreview || "/path/to/default-avatar.jpg"}
                        alt="Preview"
                        sx={{ width: 100, height: 100 }}
                      />
                    )}
                  </Box>
                  {formik.touched.image && formik.errors.image && (
                    <Box mt={1} color="error.main" textAlign="center">
                      {formik.errors.image}
                    </Box>
                  )}
                  <Box mt={2} display="flex" justifyContent="center">
                    <Button
                      size="large"
                      type="button"
                      variant="contained"
                      onClick={() => fileInputRef.current.click()}
                    >
                      {id ? "Edit" : "Upload"} Image
                    </Button>
                  </Box>
                </Grid>


                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    required
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    name="name"
                    onPaste={(e) => {
                      const pastedText = e.clipboardData.getData("text");
                      if (/^\d/.test(pastedText)) {
                        e.preventDefault();
                      }
                    }}
                    onKeyPress={handleKeyPress}
                    error={Boolean(formik.touched.name && formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Price"
                    required
                    value={formik.values.price}
                    onChange={formik.handleChange}
                    name="price"
                    error={Boolean(formik.touched.price && formik.errors.price)}
                    helperText={formik.touched.price && formik.errors.price}
                    inputProps={{ maxLength: 7 }}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    label="Earn Bids"
                    required
                    value={formik.values.earnBids}
                    onChange={formik.handleChange}
                    name="earnBids"
                    error={Boolean(
                      formik.touched.earnBids && formik.errors.earnBids
                    )}
                    helperText={
                      formik.touched.earnBids && formik.errors.earnBids
                    }
                    inputProps={{ maxLength: 7 }}
                  />
                </Grid>

                <Grid item xs={12} md={12}>
                  <FieldArray
                    name="description"
                    render={(arrayHelpers) => (
                      <Box mt={3}>
                        {formik.values.description.map((point, index) => (
                          <Box
                            key={index}
                            display="flex"
                            alignItems="center"
                            mt={2}
                          >
                            <TextField
                              fullWidth
                              label={`Description Point ${index + 1}`}
                              name={`description[${index}]`}
                              value={point}
                              onChange={formik.handleChange}
                              error={Boolean(
                                formik.touched.description?.[index] &&
                                  formik.errors.description?.[index]
                              )}
                              onKeyPress={handleKeyPress}
                              helperText={
                                formik.touched.description?.[index] &&
                                formik.errors.description?.[index]
                                  ? formik.errors.description[index]
                                  : null
                              }
                            />
                            <IconButton
                              aria-label="Remove Description Point"
                              onClick={() => arrayHelpers.remove(index)}
                              color="error"
                              disabled={formik.values.description.length === 1}
                            >
                              <RemoveCircle />
                            </IconButton>
                            <IconButton
                              aria-label="Add Description Point"
                              onClick={() => arrayHelpers.push("")}
                              color="primary"
                            >
                              <AddCircle />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  />
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} Bid Package
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditBidPackageManagement;
